import * as React from "react";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getPageContent } from "../../utils";
import { useAuth } from "../../hooks/useAuth";
import {
    selectArtWorkSelectionData,
    selectArtWorkSelectionStatus,
    selectArtWorkSelectionError,
    initializeAsync,
} from "./artWorkSelectionSlice";
import {
    Container,
    Box,
    Typography,
    Button,
    Link,
    Divider,
    Stack,
    ImageList,
    ImageListItem,
    Skeleton,
    Drawer,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert,
} from "@mui/material";
import { CustomAccordion } from "../../components/Accordion";
import { ModalContent } from "../../components/ModalContent";
import { CardContainer } from "../../components/CardContainer";
import { CustomDataTable } from "../../components/DataTable/CustomDataTable";
import { useParams, useNavigate } from "react-router-dom";
import { get, post } from "../../api";
import { CustomGrid } from "../../components/Grid/CustomGrid";
import { ImageGrid } from "../../components/ImageGrid";
import IconButton from "@mui/material/IconButton";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { useMediaQuery } from "react-responsive";
import { update } from "../../services/salesforce";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ImageUpload } from "../../components/ImageUpload";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { SimpleMasonry } from "../../components/SimpleMasonry";

const ArtWorkSelection = () => {
    const data = useSelector(selectArtWorkSelectionData);
    const status = useSelector(selectArtWorkSelectionStatus);
    const error = useSelector(selectArtWorkSelectionError);
    const dispatch = useDispatch();
    const { getUserDetailsNotAsync } = useAuth();
    const userDetails = getUserDetailsNotAsync();
    let { recordId } = useParams();
    const [openImageModal, setOpenImageModal] = useState(false);
    const [openSubmitModal, setOpenSubmitModal] = useState(false);
    const [favouriteArts, setFavouriteArts] = useState([]);
    const [loadingFavouriteArts, setLoadingFavouriteArts] = useState(true);
    const [loadingData, setLoadingData] = useState(true);
    const [selectedImage, setSelectedImage] = useState();
    const [selectedRelatedArt, setSelectedRelatedArt] = useState();
    const [selectedLocation, setSelectedLocation] = useState();
    const [orderDetails, setOrderDetails] = useState();
    const [loadingOrder, setLoadingOrder] = useState(true);
    const [loadingLocationDetails, setLoadingLocationDetails] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasIncompleteWork, setHasIncompleteWork] = useState(false);
    const [noRelatedArts, setNoRelatedArts] = useState(false);

    const [openImageWhatNextModal, setOpenImageWhatNextModal] = useState(false);
    const navigate = useNavigate();
    const [relatedArts, setRelatedArts] = useState([]);
    const [surveyLocations, setSurveyLocations] = useState([]);
    const isDesktop = useMediaQuery({ minWidth: 1180 });
    const md = useMediaQuery({ maxWidth: 900, minWidth: 768 });
    const sm = useMediaQuery({ maxWidth: 767 });
    const selectedRelatedArtRef = React.useRef();

    // Add Variables

    useEffect(() => {
        if (status === "idle") {
            dispatch(initializeAsync());
        }
    }, [status, dispatch]);

    useEffect(() => {
        if (recordId) {
            get("opportunity/getRecordById", {
                recordId: recordId,
            }).then(async (result) => {
                if (result.isSuccess) {
                    setOrderDetails(result.data[0]);
                }
                setLoadingOrder(false);
            });
            get("artLocationSurvey/getRecordById", {
                recordId: recordId,
            }).then(async (result) => {
                if (result.isSuccess) {
                    if (result.data?.length > 0) {
                        setSurveyLocations(result.data);
                    }
                    setLoadingData(false);
                } else {
                    setLoadingData(false);
                }
            });
        }
    }, [recordId]);

    useEffect(() => {
        const promises = [];
        for (let location of surveyLocations) {
            promises.push(
                get("relatedArt/getRecordById", {
                    recordId: location?.id,
                })
            );
        }
        if (promises.length > 0) {
            Promise.all(promises).then((results) => {
                let records = [];
                for (let result of results) {
                    if (result.isSuccess) {
                        records = [...records, ...result.data];
                    }
                }
                if (records.length === 0) {
                    setNoRelatedArts(true);
                    setTimeout(() => {
                        setNoRelatedArts(false);
                    }, 10000);
                }
                setRelatedArts(records);
                setLoadingLocationDetails(false);
            });
        }
    }, [loadingData]);

    const getLocationSummary = (location, index) => {
        if (!loadingData) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-between",
                    }}>
                    <Stack direction={"row"} spacing={2} sx={{ alignItems: "center" }}>
                        <Typography variant="body1">
                            <strong>{index + 1}</strong>
                        </Typography>{" "}
                        <Typography variant="body1">
                            <strong>{location?.name}</strong>, {location?.section}
                        </Typography>{" "}
                        <Divider orientation="vertical" sx={{ ml: "4px" }}></Divider>
                        <Typography variant="caption" sx={{ lineHeight: 1.6 }}>
                            {location?.height}"H{"  "}
                            {location?.width}"W
                        </Typography>
                        <Divider orientation="vertical" sx={{ ml: "4px" }}></Divider>
                        <ImageUpload
                            disableUpload={true}
                            parentId={location?.id}
                            id={"surveyLocation" + location?.id}
                            userDetails={{}}
                            title="Wall image"
                            documentType="surveyLocation"
                            height={"40px"}
                            maxWidth={"100px"}></ImageUpload>
                    </Stack>
                    <Stack direction={"row"} spacing={2} sx={{ alignItems: "center" }}>
                        <Typography variant="caption" sx={{ lineHeight: 1.6 }}>
                            <span style={{ color: "#0080ff" }}>
                                {`${relatedArtCompletedCount(location.id)} of ${relatedArtCount(location.id)} `}
                            </span>
                            pieces selected
                        </Typography>
                    </Stack>
                </Box>
            );
        }
    };

    const getFavoritesArts = async () => {
        if (userDetails?.contact?.accountId && userDetails?.contact?.id) {
            setLoadingFavouriteArts(true);

            get("favourite/getFavourite", {
                accountId: userDetails?.contact?.accountId,
                contactId: userDetails?.contact?.id,
            }).then((result) => {
                if (result.isSuccess) {
                    if (selectedRelatedArtRef.current?.orientation) {
                        let records = result.data?.filter(
                            (item) => item?.art?.orientation === selectedRelatedArtRef.current?.orientation
                        );
                        setFavouriteArts(records);
                    } else {
                        setFavouriteArts(result.data);
                    }
                }
                setLoadingFavouriteArts(false);
            });
        }
    };
    const handleImageModal = (relatedArt, location) => {
        setSelectedLocation(location);
        setSelectedRelatedArt(relatedArt);
        selectedRelatedArtRef.current = relatedArt;
        setOpenImageModal(true);
        getFavoritesArts();
    };

    const handleImageSelection = (item) => {
        let relatedArt = { ...selectedRelatedArt };
        relatedArt.artId = item.artId;
        update("relatedArt", relatedArt).then((result) => {
            if (result.isSuccess) {
                let records = [...relatedArts];
                records = records.map((relatedArtRecord) => {
                    if (relatedArtRecord.id === result.data.id) {
                        return result.data;
                    }
                    return relatedArtRecord;
                });
                setRelatedArts([...records]);
            }
        });
        setOpenImageModal(false);
    };

    const handleClick = (event) => {
        let path = event.target.id;
        navigate("/" + path, { replace: false });
    };

    const getFavoritesDetails = () => {
        if (loadingFavouriteArts) {
            let content = [1, 2, 3, 4, 5];
            return (
                <Box
                    sx={{
                        width: sm ? "auto" : "500px",
                        maxWidth: "500px",
                        height: "calc(100vh - 2.5rem)",
                        bgcolor: "white",
                    }}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ color: "black !important", fontSize: "1.2rem" }}>
                            Select artwork for location:
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: "#0080ff !important",
                                fontSize: "1.2rem",
                                mt: "5px",
                            }}
                            color="primary">
                            {selectedLocation?.name}, {selectedLocation?.section}
                        </Typography>
                    </Box>
                    <CardContainer
                        height="100%"
                        title={
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}>
                                <Typography
                                    component="div"
                                    variant="subtitle2"
                                    color="text.disabled"
                                    sx={{ color: "#909090 !important" }}>
                                    Your Favorites
                                </Typography>
                                <Link
                                    underline="always"
                                    onClick={(e) => handleClick(e)}
                                    id="favourite"
                                    sx={{ color: "#0080ff !important" }}>
                                    Edit Favorites
                                </Link>
                            </Box>
                        }
                        contentOnCenter={false}>
                        <ImageList sx={{ mt: "0px" }} cols={isDesktop ? 3 : 2} gap={1}>
                            {content?.map((item) => (
                                <ImageListItem sx={{ px: 0.5 }} key={item}>
                                    <Skeleton variant="rounded" width="100%" height="270px" />
                                </ImageListItem>
                            ))}
                        </ImageList>
                    </CardContainer>
                </Box>
            );
        }
        // if (favouriteArts?.length > 0) {
        let records = favouriteArts?.map((item) => {
            return {
                id: item.id,
                title: item.art.name,
                artId: item.art.id,
                img: process.env.REACT_APP_CLOUD_FRONT_URL + item.art?.publishedAssetLink?.asset?.fileId,
            };
        });
        return (
            <Box
                sx={{
                    width: "500px",
                    height: "calc(100vh - 4.5rem)",
                    bgcolor: "white",
                }}>
                <Box sx={{ p: 2, pb: 0 }}>
                    <Typography variant="subtitle1" sx={{ color: "black !important", fontSize: "1.2rem" }}>
                        Select artwork for location:
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: "#0080ff !important",
                            fontSize: "1.2rem",
                            mt: "5px",
                        }}
                        color="primary">
                        <Typography
                            component={"span"}
                            sx={{
                                fontSize: "1.2rem",
                                fontWeight: "700",
                                color: "#0080ff !important",
                            }}>
                            {selectedLocation?.name}
                        </Typography>
                        , {selectedLocation?.section}
                    </Typography>
                </Box>
                <CardContainer
                    height="100%"
                    title={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "-16px",
                                marginTop: "-13px",
                            }}>
                            <Typography
                                component="div"
                                variant="subtitle2"
                                color="text.disabled"
                                sx={{ color: "#909090 !important" }}>
                                Your Favorites
                            </Typography>
                            <Link
                                underline="always"
                                onClick={(e) => handleClick(e)}
                                id="favourite"
                                sx={{ color: "#0080ff !important" }}>
                                Edit Favorites
                            </Link>
                        </Box>
                    }
                    contentOnCenter={favouriteArts?.length > 0 || loadingFavouriteArts ? false : true}>
                    {records?.length > 0 ? (
                        // <ImageGrid content={records} onImageSelection={handleImageSelection}></ImageGrid>
                        <SimpleMasonry content={records} onImageSelection={handleImageSelection}></SimpleMasonry>
                    ) : (
                        <Box align="center" sx={{ mt: 2, py: 2, background: "white" }}>
                            <Typography
                                variant="h5"
                                align="center"
                                color="text.disabled"
                                gutterBottom
                                sx={{ maxWidth: "500px" }}>
                                Your favourite art work will appear here.
                            </Typography>
                            <Button sx={{ mt: 4 }} variant="outlined" onClick={(e) => handleClick(e)} id="favourite">
                                Browse Gallery
                            </Button>
                        </Box>
                    )}
                </CardContainer>
            </Box>
        );
        // }
        // return (
        //     <Box align="center" sx={{ mt: 2, py: 2, background: "white" }}>
        //         <Typography variant="h5" align="center" color="text.disabled" gutterBottom sx={{ maxWidth: "500px" }}>
        //             Your favourite art work will appear here.
        //         </Typography>
        //         <Button sx={{ mt: 4 }} variant="outlined" onClick={(e) => handleClick(e)} id="favourite">
        //             Browse Gallery
        //         </Button>
        //     </Box>
        // );
    };

    const numberToAlphabet = (number) => {
        if (number < 1 || number > 26) {
            return "Number out of range";
        }

        // Adding 64 to convert the number to ASCII code of corresponding alphabet
        return String.fromCharCode(number + 64);
    };

    const relatedArtCount = (locationId) => {
        let artPieces = relatedArts?.filter((art) => art?.artLocationId === locationId);
        return artPieces.length > 0 ? artPieces.length : 0;
    };

    const relatedArtCompletedCount = (locationId) => {
        let artPieces = relatedArts?.filter((art) => art?.artLocationId === locationId && art.artId);
        return artPieces.length > 0 ? artPieces.length : 0;
    };

    const getFormInMobileView = (index, piece, location) => {
        if (loadingLocationDetails) {
            console.log("Still loading");
        } else {
            //console.log("Piece :: ", piece);
            return (
                <Box
                    sx={{
                        width: "100%",
                        textAlign: "center",
                    }}>
                    <Grid xs={12} container sx={{ borderBottom: "1px solid", borderColor: "#E8E8E8", height: "100%" }}>
                        <Grid
                            item
                            xs={4}
                            sx={{
                                borderRight: "1px solid",
                                borderColor: "#E8E8E8",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                            }}>
                            <Stack spacing={1}>
                                <Typography variant="subtitle1" color="primary">
                                    {numberToAlphabet(index + 1)}
                                </Typography>
                            </Stack>
                        </Grid>

                        <Grid item xs={8} sx={{ p: 1 }}>
                            <Stack spacing={1} sx={{ display: "flex", alignItems: "center" }}>
                                {!piece?.artId ? (
                                    <Box
                                        sx={{
                                            height: "100%",
                                            width: "120px",
                                            display: "flex",
                                            justifyContent: "center",
                                            flexDirection: "column",
                                        }}>
                                        <Box sx={{}}>
                                            <IconButton
                                                sx={{ width: "40px" }}
                                                color="primary"
                                                onClick={() => handleImageModal(piece, location)}>
                                                <ControlPointIcon />
                                            </IconButton>
                                        </Box>
                                        <Typography variant="caption" color="text.primary">
                                            Select Art
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <Box sx={{ display: "flex", justifyContent: "center", height: "100%" }}>
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    height: "100%",
                                                    width: "120px",
                                                    padding: "10px 0",
                                                }}>
                                                <img
                                                    src={process.env.REACT_APP_CLOUD_FRONT_URL + piece.artFileId}
                                                    style={{
                                                        height: "100%",
                                                        width: "120px",
                                                        borderRadius: "5px",
                                                        objectFit: "cover",
                                                    }}
                                                    alt="Art"></img>
                                                {orderDetails?.status !== "Artwork Submitted" && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            right: "4px",
                                                            top: "14px",
                                                            border: "2px solid #2c95f0",
                                                            borderRadius: "50px",
                                                            height: "28px",
                                                            width: "28px",
                                                            background: "white",
                                                        }}>
                                                        <RefreshIcon
                                                            sx={{ color: "#2c95f0" }}
                                                            onClick={() =>
                                                                handleImageModal(piece, location)
                                                            }></RefreshIcon>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            width: "100%",
                            textAlign: "center",
                        }}>
                        <Grid xs={12} container>
                            <Grid
                                item
                                xs={4}
                                sx={{
                                    borderRight: "1px solid",
                                    borderColor: "#E8E8E8",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    p: 1,
                                    height: "100%",
                                }}>
                                <Stack spacing={1}>
                                    <Typography variant="caption" color="text.primary">
                                        <strong>{piece?.size}</strong>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Art Size
                                    </Typography>
                                    <Typography variant="caption" color="text.primary">
                                        <strong>
                                            {piece?.height}"H x {piece?.width}"W
                                        </strong>
                                    </Typography>
                                </Stack>
                            </Grid>

                            <Grid
                                item
                                xs={4}
                                sx={{
                                    borderRight: "1px solid",
                                    borderColor: "#E8E8E8",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                }}>
                                <Stack spacing={1}>
                                    <Typography variant="caption" color="text.primary">
                                        <strong>{piece?.orientation}</strong>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Orientation
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                }}>
                                <Stack spacing={1}>
                                    {piece?.note && (
                                        <Box>
                                            <Tooltip title={piece?.note}>
                                                <IconButton sx={{ width: "40px" }} color="primary">
                                                    <ArticleOutlinedIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    {!piece?.note && (
                                        <Box>
                                            <Tooltip title={"No notes"}>
                                                <IconButton sx={{ width: "40px", color: "#e8e8e8" }}>
                                                    <ArticleOutlinedIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            );
        }
    };

    const getPiecesDetails = (piece, index, location) => {
        console.log("Piece :: ", piece);
        const items = [
            {
                body: (
                    <Stack spacing={1} sx={{ textAlign: "center" }}>
                        <Typography variant="subtitle1" color="primary">
                            {numberToAlphabet(index + 1)}
                        </Typography>
                    </Stack>
                ),
                size: 1,
            },
            {
                body: (
                    <Stack spacing={1} sx={{ textAlign: "center" }}>
                        <Typography variant="caption" color="text.primary">
                            <strong>{piece?.orientation}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Orientation
                        </Typography>
                    </Stack>
                ),
            },
            {
                body: (
                    <Stack spacing={1} sx={{ textAlign: "center" }}>
                        <Typography variant="caption" color="text.primary">
                            <strong>{piece?.size}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Art Size
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                            <strong>
                                {piece?.height}"H x {piece?.width}"W
                            </strong>
                        </Typography>
                    </Stack>
                ),
            },
            {
                body: (
                    <Stack spacing={1} sx={{ textAlign: "center" }}>
                        {!piece?.artId ? (
                            <>
                                <Box>
                                    <IconButton
                                        sx={{ width: "40px" }}
                                        color="primary"
                                        onClick={() => handleImageModal(piece, location)}>
                                        <ControlPointIcon />
                                    </IconButton>
                                </Box>
                                <Typography variant="caption" color="text.primary">
                                    Select Art
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            height: "100%",
                                            width: "120px",
                                            padding: "10px 0",
                                        }}>
                                        <img
                                            src={process.env.REACT_APP_CLOUD_FRONT_URL + piece.artFileId}
                                            style={{
                                                height: "100%",
                                                width: "120px",
                                                borderRadius: "5px",
                                                objectFit: "cover",
                                            }}
                                            alt="Art"></img>
                                        {orderDetails?.status === "Survey Approved" && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    right: "4px",
                                                    top: "14px",
                                                    border: "2px solid #2c95f0",
                                                    borderRadius: "50px",
                                                    height: "28px",
                                                    width: "28px",
                                                    background: "white",
                                                }}>
                                                <RefreshIcon
                                                    sx={{ color: "#2c95f0" }}
                                                    onClick={() => handleImageModal(piece, location)}></RefreshIcon>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Stack>
                ),
                size:
                    orderDetails?.status === "Artwork Selection Approved" || orderDetails?.status === "Completed"
                        ? 3
                        : 5,
            },

            {
                body: (
                    <Stack spacing={1} sx={{ textAlign: "center" }}>
                        {piece?.note && (
                            <Box>
                                <Tooltip title={piece?.note}>
                                    <IconButton sx={{ width: "40px" }} color="primary">
                                        <ArticleOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        {!piece?.note && (
                            <Box>
                                <Tooltip title={"No notes"}>
                                    <IconButton sx={{ width: "40px", color: "#e8e8e8" }}>
                                        <ArticleOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Stack>
                ),
            },
        ];
        if (orderDetails?.status === "Artwork Selection Approved" || orderDetails?.status === "Completed") {
            items.splice(4, 0, {
                body: (
                    <Stack spacing={1} sx={{ textAlign: "center" }}>
                        <Typography variant="caption" color="text.primary">
                            <strong>
                                {new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    maximumFractionDigits: 0,
                                }).format(piece?.donation)}
                            </strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Donation
                        </Typography>
                    </Stack>
                ),
            });
        }

        return items;
    };
    const getLocationDetails = (location, index) => {
        if (!loadingLocationDetails) {
            if (relatedArts?.length > 0) {
                let artPieces = relatedArts?.filter((art) => art?.artLocationId === location?.id);

                return artPieces?.map((piece, index) => {
                    return isDesktop ? (
                        <Box sx={{ mb: "10px" }}>
                            <CustomGrid
                                text={"primary"}
                                border={true}
                                borderleft={true}
                                borderRight={true}
                                borderBottom={false}
                                rowHeight={"100%"}
                                records={getPiecesDetails(piece, index, location)}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ mb: 2 }}>
                            <CardContainer px="0px" py="0px" isOutlined={true} contentOnCenter={false}>
                                {getFormInMobileView(index, piece, location)}
                            </CardContainer>
                        </Box>
                    );
                });
            }
        }
    };

    const handleUpdateOpportunityStatus = () => {
        let record = { ...orderDetails };
        setIsSubmitting(true);
        record.status = "Artwork Submitted";
        post("opportunity/createOrder", record).then((result) => {
            if (result.isSuccess) {
                setOrderDetails(result.data);
            } else {
                setIsSubmitting(false);
            }
            setOpenSubmitModal(false);
        });
    };
    const handleOpenModal = (result) => {
        if (relatedArts?.length === 0) {
            setNoRelatedArts(true);
            setTimeout(() => {
                setNoRelatedArts(false);
            }, 3000);
        }

        let incomplete = relatedArts.find((item) => !item.artId);
        if (incomplete) {
            setHasIncompleteWork(true);
            setTimeout(() => {
                setHasIncompleteWork(false);
            }, 3000);
        } else {
            setOpenSubmitModal(true);
        }
    };

    const getReviewRecords = () => {
        let totalDonation = 0;
        let totalInstallCost = 0;
        let totalPieces = 0;
        let recordsObjects = {};
        let locationObjects = {};
        for (let location of surveyLocations) {
            locationObjects[location.id] = location;
        }
        for (let art of relatedArts) {
            if (!recordsObjects[art.artLocationId]) {
                recordsObjects[art.artLocationId] = {
                    name: `${locationObjects[art.artLocationId]?.name}`,
                    donation: 0,
                    installCost: 0,
                    pieces: 0,
                };
            }
            recordsObjects[art.artLocationId] = {
                ...recordsObjects[art.artLocationId],
                donation: recordsObjects[art.artLocationId].donation + art.donation,
                installCost: recordsObjects[art.artLocationId].installCost + art.installationCost,
                pieces: recordsObjects[art.artLocationId].pieces + 1,
            };
            totalDonation += art.donation;
            totalInstallCost += art.installationCost;
            totalPieces += 1;
        }
        let totalPrice = totalDonation + totalInstallCost;
        let records = Object.values(recordsObjects);
        return (
            <Box>
                <Box sx={{ p: "16px 24px 8px 24px" }}>
                    {records.map((item) => {
                        return (
                            <Box
                                sx={{
                                    background: "#e6eef5",
                                    marginBottom: "8px",
                                    padding: "16px 0px",
                                    borderRadius: "8px",
                                }}>
                                <Grid sx={{ p: "0 12px", fontSize: ".9rem", fontWeight: "400" }} container>
                                    <Grid xs={10} item>
                                        <span style={{ fontWeight: "500" }}>{item.name}</span>
                                    </Grid>

                                    <Grid xs={2} sx={{ textAlign: "center" }} item>
                                        {item.pieces}
                                    </Grid>
                                </Grid>
                            </Box>
                        );
                    })}
                </Box>
                <Divider sx={{}} />
                <Box sx={{ p: "16px 24px", color: "#0080ff" }}>
                    <Grid sx={{ p: "0 12px", fontSize: ".9rem", fontWeight: "400" }} container>
                        <Grid xs={10} item>
                            <span style={{ fontWeight: "500" }}>Total</span>
                        </Grid>

                        <Grid xs={2} sx={{ textAlign: "center" }} item>
                            {totalPieces}
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        );
    };

    const handleOpenWhatNextModal = (value) => {
        setOpenImageWhatNextModal(value);
    };

    const handleBrowseGallery = () => {
        navigate("/favourite", { replace: false });
    };

    const handleGoToDashboard = () => {
        navigate("/", { replace: false });
    };

    let body = (
        <>
            {!loadingOrder && (
                <Container maxWidth>
                    {hasIncompleteWork && (
                        <Alert
                            sx={{ position: "absolute" }}
                            severity="error"
                            onClose={() => setHasIncompleteWork(false)}>
                            Select art for all pieces.
                        </Alert>
                    )}
                    {noRelatedArts && (
                        <Alert
                            sx={{ position: "absolute" }}
                            severity="error"
                            onClose={() => setHasIncompleteWork(false)}>
                            No related art selected, please contact with Fresh Artists.
                        </Alert>
                    )}
                    <Grid xs={12} container spacing={2} sx={{ justifyContent: "space-between" }}>
                        <Grid item xs={12} md={8} sx={{ textAlign: isDesktop ? "left" : "center" }}>
                            <Typography variant="h3">Location Art Selection</Typography>
                            <Typography variant="subtitle1" color="text.disabled">
                                <strong>{orderDetails.name}</strong>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={"auto"} sx={{ textAlign: isDesktop ? "right" : "center" }}>
                            {orderDetails?.status === "Artwork Submitted" ||
                            orderDetails?.status === "Artwork Selection Approved" ||
                            orderDetails?.status === "Completed" ? (
                                <Box
                                    sx={{
                                        border: 1,
                                        borderRadius: 1,
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 2,
                                    }}>
                                    <Typography variant="body1">Your survey is currently under review !</Typography>{" "}
                                    <Button
                                        variant="text"
                                        sixe="small"
                                        sx={{ p: 0, ml: 1 }}
                                        onClick={() => handleOpenWhatNextModal(true)}>
                                        What's next?
                                    </Button>
                                </Box>
                            ) : (
                                <Button variant="contained" color="success" onClick={handleOpenModal}>
                                    Submit artwork selections
                                </Button>
                            )}
                        </Grid>
                    </Grid>

                    {orderDetails?.surveyApproveMessage && (
                        <Box
                            sx={{
                                width: "100%",
                                bgcolor: "white",
                                p: 2,
                                border: "1px solid",
                                borderColor: "text.disabled",
                                borderRadius: 2,
                            }}>
                            <Typography variant="subtitle1" sx={{ lineHeight: "20px" }}>
                                {orderDetails?.surveyApproveMessage}
                            </Typography>
                        </Box>
                    )}
                    {!loadingData &&
                        surveyLocations?.map((location, index) => {
                            return (
                                <CustomAccordion
                                    summary={getLocationSummary(location, index)}
                                    details={getLocationDetails(location, index)}
                                    defaultExpanded={index === 0}
                                />
                            );
                        })}
                    {openImageModal && (
                        <Drawer
                            anchor={"right"}
                            sx={{ minWidth: "300px" }}
                            open={openImageModal}
                            onClose={() => setOpenImageModal(false)}>
                            {getFavoritesDetails()}
                        </Drawer>
                        // <ModalContent open={openImageModal} isSurvey={true} content={getFavoritesDetails()}></ModalContent>
                    )}
                    {openImageWhatNextModal && (
                        <ModalContent openModal={handleOpenWhatNextModal} open={openImageWhatNextModal} isSurvey={true}>
                            <Box sx={{ maxWidth: "20rem", m: "0px auto" }}>
                                <Typography
                                    variant="subtitle1"
                                    color="white"
                                    sx={{ wordSpacing: "1px", lineHeight: "18px" }}>
                                    Great Work!
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    color="white"
                                    sx={{ wordSpacing: "1px", lineHeight: "18px" }}>
                                    We are currently reviewing your your art selection, we will back to you in 5-8
                                    business days with next steps.
                                </Typography>

                                <Typography
                                    sx={{ mt: "1rem", wordSpacing: "1px", lineHeight: "18px" }}
                                    variant="subtitle1"
                                    color="white">
                                    In the meantime, feel free to browse the artwork gallery and pieces to your
                                    favourite collection.
                                </Typography>

                                <Button
                                    sx={{ mt: 1, right: "0px !important" }}
                                    variant="contained"
                                    size="large"
                                    id="addSurvey"
                                    color="primary"
                                    onClick={handleBrowseGallery}>
                                    Browse Gallery
                                </Button>

                                <Button
                                    sx={{ mt: 1, right: "0px !important", color: "white" }}
                                    variant="text"
                                    size="large"
                                    id="addSurvey"
                                    color="info"
                                    onClick={handleGoToDashboard}>
                                    Go to Dashboard
                                </Button>
                            </Box>
                        </ModalContent>
                    )}
                    {openSubmitModal && (
                        <Dialog
                            open={openSubmitModal}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description">
                            <DialogTitle
                                id="alert-dialog-title"
                                sx={{ color: "black", borderBottom: "1px solid #dfdfdf" }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography variant="h3">Donation Project Review</Typography>
                                    <IconButton
                                        sx={{ mr: "-10px", mt: "-5px" }}
                                        onClick={() => setOpenSubmitModal(false)}>
                                        <CloseIcon></CloseIcon>
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }} color="text.disabled">
                                        Prepared for{" "}
                                        <span style={{ color: "#0080ff" }}>{orderDetails.accountName}</span>
                                    </Typography>
                                </Box>
                            </DialogTitle>
                            <DialogContent sx={{ p: 0, minWidth: "500px" }}>
                                <Box sx={{ p: "16px 24px" }}>
                                    <Typography variant="subtitle1">{orderDetails.name}</Typography>
                                    <Grid
                                        sx={{
                                            mt: "10px",
                                            p: "0 12px 0 0",
                                            fontSize: ".9rem",
                                            color: "text.disabled",
                                            fontWeight: "400",
                                        }}
                                        container>
                                        <Grid xs={10} item>
                                            Section
                                        </Grid>

                                        <Grid xs={2} sx={{ textAlign: "center" }} item>
                                            Pieces
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Divider sx={{}} />
                                {getReviewRecords()}
                            </DialogContent>
                            <DialogActions sx={{ p: "16px 24px", borderTop: "1px solid #dfdfdf" }}>
                                <Box
                                    sx={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        flexDirection: "column",
                                    }}>
                                    <LoadingButton
                                        loading={isSubmitting}
                                        onClick={handleUpdateOpportunityStatus}
                                        variant="contained"
                                        size="large"
                                        color="success">
                                        Submit project
                                    </LoadingButton>
                                    <Typography
                                        sx={{
                                            marginInline: "auto",
                                            textAlign: "center",
                                            mt: "1rem",
                                        }}
                                        variant="caption"
                                        color="black">
                                        Have a question about your project?{" "}
                                        <Link href="#" underline="always">
                                            Contact us
                                        </Link>
                                    </Typography>{" "}
                                </Box>
                            </DialogActions>
                        </Dialog>
                    )}
                </Container>
            )}
        </>
    );

    return getPageContent(body, status, error);
};

export { ArtWorkSelection };
